import { PRICE_CURRENCY_OPTIONS } from "@constants";

import type { InfiniteData } from "@tanstack/react-query";

import type { IApiResponse, IPagination } from "@api";
import type { IPrice } from "@formControl/priceInput/types";
import type {
  ID,
  IDtoPrice,
  IFileObject,
  IFileResponse,
  IOption,
  IResponseOption,
} from "@interfaces";

export const collectionMapperFunc = <TApi, TData>(
  data: IApiResponse<IPagination<TApi>>,
  mapper: (data: TApi) => TData,
): IPagination<TData> => {
  return {
    ...data?.data,
    items: data?.data?.items?.map((item) => mapper(item)),
  };
};

export const infiniteMapperFunc = <TApi, TData, TCustomData extends object>(
  data: InfiniteData<IApiResponse<IPagination<TApi>>>,
  mapper: (data: IApiResponse<IPagination<TApi>>) => IPagination<TData>,
  customMapper?: (data: IApiResponse<IPagination<TApi>>) => TCustomData,
): IPagination<TData> & TCustomData => {
  const mapperData = data?.pages?.[0]
    ? customMapper?.(data?.pages?.[0])
    : ({} as TCustomData);

  return {
    ...(mapperData || ({} as TCustomData)),
    current_page: data?.pages?.[0]?.data?.current_page || 1,
    includes: data?.pages?.[0]?.data?.includes,
    per_page: data?.pages?.[0]?.data?.per_page || 10,
    total_items: data?.pages?.[0]?.data?.total_items || 0,
    total_pages: data?.pages?.[0]?.data?.total_pages || 0,
    items: data?.pages?.flatMap((innerData) => mapper(innerData)?.items) || [],
  };
};

export const toOption = <T extends ID = ID>(
  data: { id: T; name: string } | undefined,
): IOption<T> => {
  return { key: data?.id || ("" as T), value: data?.name || "" };
};

export const toPrice = (
  data:
    | {
        amount?: string;
        currency?: IResponseOption;
      }
    | undefined,
): IPrice => {
  const { amount, currency } = data || {};

  return {
    key: currency?.id || PRICE_CURRENCY_OPTIONS?.[0]?.key || "",
    value: currency?.name || PRICE_CURRENCY_OPTIONS?.[0]?.value || "",
    // flag: currency?.flag || PRICE_CURRENCY_OPTIONS?.[0]?.flag,
    price: amount?.toString() || "",
  };
};

export const toDtoPrice = (data: IPrice): IDtoPrice => {
  const { price, key } = data;

  return {
    amount: price,
    currency_id: key,
  };
};

export function toIFileObject(fileResponse: IFileResponse): IFileObject {
  if (!fileResponse) {
    return null;
  }
  return {
    id: fileResponse.id,
    name: fileResponse.name,
    size: fileResponse.size_bytes,
    type: fileResponse.mime_type,
    url: fileResponse.url,
  };
}
